import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ArrowLeft, ChevronDown, Trash2 } from 'lucide-react';
import { getConversations, getMessages, sendMessage, getUnreadCount, deleteMessage } from '../../api/messages';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from '../../utils/constants';

export function ChatPanel() {
  const { user } = useAuth();
  const { socket } = useSocket();

  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null); // { user_id, name }
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);

  // Okunmamış sayısını yükle
  const loadUnread = useCallback(async () => {
    try { const d = await getUnreadCount(); setUnread(d.count); } catch {}
  }, []);

  // Sohbet listesini yükle
  const loadConversations = useCallback(async () => {
    try { const d = await getConversations(); setConversations(d.conversations); } catch {}
  }, []);

  // Mesajları yükle
  const loadMessages = useCallback(async (userId) => {
    try {
      const d = await getMessages(userId);
      setMessages(d.messages);
      // Scroll aşağı
      setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {}
  }, []);

  useEffect(() => { loadUnread(); }, [loadUnread]);
  useEffect(() => { if (open && !activeChat) loadConversations(); }, [open, activeChat, loadConversations]);

  // Socket.io dinle
  useEffect(() => {
    if (!socket) return;
    const handler = (msg) => {
      // Aktif sohbetteyse mesajı ekle
      if (activeChat && (msg.sender_id === activeChat.user_id || msg.receiver_id === activeChat.user_id)) {
        setMessages(prev => [...prev, msg]);
        setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }
      // Unread güncelle
      if (msg.sender_id !== user?.id) {
        if (!open || !activeChat || activeChat.user_id !== msg.sender_id) {
          setUnread(c => c + 1);
        }
        loadConversations();
      }
    };
    const deleteHandler = (data) => {
      setMessages(prev => prev.map(m => m.id === data.id ? { ...m, deleted_at: new Date().toISOString(), body: null } : m));
    };
    socket.on('message:new', handler);
    socket.on('message:deleted', deleteHandler);
    return () => { socket.off('message:new', handler); socket.off('message:deleted', deleteHandler); };
  }, [socket, activeChat, open, user?.id, loadConversations]);

  const openChat = (conv) => {
    setActiveChat({ user_id: conv.user_id, name: conv.name });
    loadMessages(conv.user_id);
    // Okunmamışları güncelle
    if (conv.unread_count > 0) {
      setUnread(c => Math.max(0, c - conv.unread_count));
      setConversations(prev =>
        prev.map(c => c.user_id === conv.user_id ? { ...c, unread_count: 0 } : c)
      );
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !activeChat || sending) return;
    setSending(true);
    try {
      await sendMessage({ receiver_id: activeChat.user_id, body: draft.trim() });
      setDraft('');
      inputRef.current?.focus();
    } catch {}
    setSending(false);
  };

  const handleBack = () => {
    setActiveChat(null);
    setMessages([]);
    loadConversations();
  };

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => { setOpen(o => !o); if (!open) loadUnread(); }}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition z-50"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 w-80 h-[480px] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden z-50">
          {/* Header */}
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center gap-3 shrink-0">
            {activeChat && (
              <button onClick={handleBack} className="hover:bg-slate-800 p-1 rounded">
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm truncate">{activeChat ? activeChat.name : 'Mesajlar'}</p>
              {!activeChat && <p className="text-xs text-slate-400">Ekip Sohbeti</p>}
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-slate-800 p-1 rounded">
              <X size={16} />
            </button>
          </div>

          {!activeChat ? (
            /* Sohbet Listesi */
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-12">Henüz sohbet yok</p>
              ) : (
                conversations.map(c => (
                  <button
                    key={c.user_id}
                    onClick={() => openChat(c)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-100 text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-bold shrink-0">
                      {c.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                        {c.last_message_at && (
                          <span className="text-[10px] text-slate-400 shrink-0 ml-2">
                            {formatTime(c.last_message_at)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500 truncate">
                          {c.last_message || <span className="text-slate-300">{ROLE_LABELS[c.role] || c.role}</span>}
                        </p>
                        {c.unread_count > 0 && (
                          <span className="w-5 h-5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0 ml-2">
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            /* Mesaj Görünümü */
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">
                {messages.map(m => {
                  const isMe = m.sender_id === user?.id;
                  const isDeleted = !!m.deleted_at;
                  return (
                    <div key={m.id} className={`group flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      {isMe && !isDeleted && (
                        <button
                          onClick={async () => {
                            try {
                              await deleteMessage(m.id);
                              setMessages(prev => prev.map(msg => msg.id === m.id ? { ...msg, deleted_at: new Date().toISOString(), body: null } : msg));
                            } catch {}
                          }}
                          className="opacity-0 group-hover:opacity-100 self-center mr-1 p-1 rounded hover:bg-slate-200 transition"
                          title="Mesajı sil"
                        >
                          <Trash2 size={12} className="text-slate-400" />
                        </button>
                      )}
                      <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${
                        isDeleted
                          ? 'bg-slate-100 text-slate-400 italic border border-slate-200'
                          : isMe
                            ? 'bg-blue-600 text-white rounded-br-sm'
                            : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                      }`}>
                        <p className="break-words">{isDeleted ? 'Bu mesaj silindi' : m.body}</p>
                        <p className={`text-[10px] mt-1 ${isDeleted ? 'text-slate-300' : isMe ? 'text-blue-200' : 'text-slate-400'}`}>
                          {formatTime(m.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEnd} />
              </div>

              {/* Gönder */}
              <form onSubmit={handleSend} className="border-t px-3 py-2 flex gap-2 shrink-0">
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  placeholder="Mesaj yaz..."
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!draft.trim() || sending}
                  className="w-9 h-9 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 disabled:opacity-40 transition shrink-0"
                >
                  <Send size={15} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 172800000) return 'Dün';
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}
